/**
 * useDecisionRightGate — ask, at the point of acting, whether this user holds
 * the decision right for what they are about to do.
 *
 * Loads the cycle's configured rights (once, cached per cycle by the store) and
 * evaluates them with `evaluateDecisionRight`, which fails open when the cycle
 * has no rights configured. A caller can therefore mount this unconditionally:
 * on a cycle nobody has configured, `allowed` is true and `configured` is
 * false, and the caller should render no governance affordance at all.
 *
 * This is the UI affordance, not the control. Whether a governed write actually
 * lands is decided by the RPC that commits it. Use this to decide what to
 * offer and what to explain — never as the only barrier.
 */

import { useEffect, useMemo } from 'react';
import { useDecisionRightsStore } from '@/stores/prioritization/decisionRightsStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import {
  evaluateDecisionRight,
  type DecisionRightEvaluation,
} from '@/lib/decisionRightsEnforcement';
import type { DecisionExecutionMode } from '@/types/prioritization/decisionRights';
import type {
  CanonicalSessionType,
  CanonicalDecisionClassKind,
} from '@/types/prioritization/session';

export interface UseDecisionRightGateParams {
  cycleId: string | null | undefined;
  domain: CanonicalSessionType;
  decisionClass: CanonicalDecisionClassKind;
  /** Track id, attribute key or item category. Defaults to the wildcard. */
  scopeSelector?: string;
  /** The mode the user would be acting in. */
  mode: DecisionExecutionMode;
  /** Role/capability ids the user holds in this cycle's scope. */
  userRoleIds?: string[];
}

export interface DecisionRightGate extends DecisionRightEvaluation {
  /** True while the cycle's rights are still loading. */
  loading: boolean;
}

const NO_ROLES: string[] = [];

export function useDecisionRightGate({
  cycleId,
  domain,
  decisionClass,
  scopeSelector,
  mode,
  userRoleIds = NO_ROLES,
}: UseDecisionRightGateParams): DecisionRightGate {
  const userId = useUserStore((s) => s.userId);
  const loadRights = useDecisionRightsStore((s) => s.loadRights);
  const rightsByCycle = useDecisionRightsStore((s) => s.rightsByCycle);
  const delegationsByCycle = useDecisionRightsStore((s) => s.delegationsByCycle);
  const loading = useDecisionRightsStore((s) => (cycleId ? !!s.loading[cycleId] : false));

  useEffect(() => {
    if (cycleId) void loadRights(cycleId);
  }, [cycleId, loadRights]);

  // Rebuild the configuration from the store slices rather than calling
  // getConfigForCycle() in render: subscribing to the slices is what makes this
  // re-evaluate when rights change, and it keeps the memo key honest.
  const evaluation = useMemo<DecisionRightEvaluation>(() => {
    if (!cycleId || !userId) {
      return {
        allowed: true,
        configured: false,
        holder: 'unconfigured',
        canDelegate: false,
        canReassign: false,
      };
    }
    const rights = rightsByCycle[cycleId];
    if (!rights || rights.length === 0) {
      return {
        allowed: true,
        configured: false,
        holder: 'unconfigured',
        canDelegate: false,
        canReassign: false,
      };
    }
    return evaluateDecisionRight(
      {
        cycleId,
        rights,
        delegations: delegationsByCycle[cycleId] ?? [],
        version: 1,
        configuredAt: new Date(),
        configuredBy: '',
      },
      { domain, decisionClass, scopeSelector, mode, userId, userRoleIds }
    );
  }, [
    cycleId,
    userId,
    rightsByCycle,
    delegationsByCycle,
    domain,
    decisionClass,
    scopeSelector,
    mode,
    userRoleIds,
  ]);

  return { ...evaluation, loading };
}
